// Konta rodziny i profile dzieci po stronie aplikacji: co pokazać na starcie (logowanie, „Kto się uczy?”, ekran główny), skąd brać postęp
// wybranego profilu i kiedy odesłać go na serwer. Sama sieć jest w profilesApi.ts, scalanie w progressMerge.ts, wysyłka w sync.ts.
//
// Dwa tryby:
//  • `family` — serwer jest: urządzenie ma klucz (parowanie PIN-em rodzica), dziecko wybiera profil (zawsze, na świeżym starcie; profil z PIN-em pyta o PIN),
//    postęp jest zapisywany na serwerze; kopia w przeglądarce to tylko robocza (praca bez internetu). Wyczyszczenie przeglądarki niczego nie kasuje — po zalogowaniu PIN-em postęp wraca.
//    Odświeżenie strony zostaje w tej samej sesji (profil, powitanie i ekran są w pamięci sesji — lib/route.ts), a „Zmień profil” i „Wyloguj” sesję kończą.
//  • `local` — ten adres nie ma serwera (dev, hosting statyczny): jak dotąd, jeden postęp w tej przeglądarce.
import { DEFAULT_LOOK, cleanProfileName, lookOf } from './look.ts';
import type { Look } from './look.ts';
import { isPin, localPinDigest } from './pinLocal.ts';
import type { ProfilesClient, ProfileInfo, RankingResult, VerifyResult } from './profilesApi.ts';
import type { ProfileStore } from './profileStore.ts';
import { emptyProgress } from './progress.ts';
import { HOME_ROUTE, LOCAL_PROFILE } from './route.ts';
import type { Route } from './route.ts';
import { createSync } from './sync.ts';
import type { Sync, SyncEvent, SyncState, Timers } from './sync.ts';
import type { Pair } from './i18n.ts';
import type { Progress, TeacherLang } from '../types.ts';

/** Co aplikacja ma pokazać (wynik `start()`). */
export type Entry =
  /** Profil (albo tryb lokalny) jest gotowy — ekran główny. */
  | { screen: 'home' }
  /** Trzeba wpisać PIN rodzica. `hadKey`: urządzenie miało klucz, ale serwer go nie przyjął (wyłączone w panelu albo nieważny). */
  | { screen: 'login'; hadKey: boolean }
  /** „Kto dziś się uczy?”. `offline`: lista pochodzi z pamięci przeglądarki, bo nie ma połączenia. `notice` jest dla dziecka, więc w języku podanym w `start(lang)`. */
  | { screen: 'pick'; profiles: ProfileInfo[]; offline: boolean; /** Czy jest miejsce na kolejny profil i serwer odpowiada (kafelek „+ Dodaj profil”). */ canAdd: boolean; notice?: string }
  /** Urządzenie jest sparowane, ale na serwerze nie ma jeszcze żadnego profilu — rodzic zakłada pierwszy. */
  | { screen: 'create-first' }
  /** Brak internetu, a przeglądarka nic jeszcze nie wie o koncie. */
  | { screen: 'offline' };

export interface AccountEvent extends SyncEvent {
  profileId: string;
}

export interface Accounts {
  /** Ustala, co pokazać: sprawdza serwer, wybrany wcześniej profil i pamięć przeglądarki. `lang` — język komunikatu dla dziecka (`notice`); domyślnie polski. */
  start(lang?: TeacherLang): Promise<Entry>;
  /** Czy jest już aktywny profil (albo tryb lokalny), czyli można pokazać ekran główny. */
  active(): boolean;
  mode(): 'none' | 'local' | 'family';
  /** Wybrany profil; null w trybie lokalnym i przed wyborem. */
  profile(): ProfileInfo | null;
  /** Ostatnia znana lista profili (do „Zmień profil”). */
  profiles(): readonly ProfileInfo[];
  look(): Look;
  progress(): Progress;
  setProgress(p: Progress): void;
  /** Wybór profilu z ekranu „Kto dziś się uczy?” (PIN profilu ekran sprawdza wcześniej przez `verifyPin`). Czeka krótko na pierwsze pobranie postępu, jeśli to urządzenie jeszcze go nie ma. */
  select(id: string): Promise<boolean>;
  /** Sprawdza PIN profilu: na serwerze (z blokadą po 5 błędach), a bez internetu — ze skrótem zapamiętanym po ostatnim udanym wejściu. Profil bez PIN-u zawsze przechodzi. */
  verifyPin(id: string, pin: string): Promise<VerifyResult>;
  /** Ekran, na którym dziecko jest w tej sesji (po odświeżeniu wracamy na niego); bez sesji — ekran główny. */
  route(): Route;
  setRoute(route: Route): void;
  /** Czy powitanie „Zaczynamy” było już w tej sesji (odświeżenie go nie powtarza; wybór profilu i wylogowanie tak). */
  welcomed(): boolean;
  markWelcomed(): void;
  /** Profil właśnie założony przez rodzica na tym urządzeniu — od razu go wybiera. */
  adopt(profile: ProfileInfo): Promise<void>;
  /** „Zmień profil”: dosyła zmiany na serwer (chwilę czeka), kończy sesję i wraca do wyboru. */
  leave(): Promise<void>;
  /** Kończy sesję bez dosyłania (odłączenie urządzenia, wyłączone konto). */
  signOut(): void;
  /** Dziecko zmienia swój wygląd; zapisuje się od razu lokalnie, a na serwer — jeśli się da. */
  setLook(look: Partial<Look>): void;
  /** Dziecko zmienia swoje imię (ołówek w „Mój wygląd”). Zwraca '' po powodzeniu albo komunikat dla dziecka w języku `lang` (domyślnie polski): imię zajęte, nieprawidłowe, brak połączenia. W trybie lokalnym nie ma imienia. */
  rename(name: string, lang?: TeacherLang): Promise<string>;
  /** Pobiera listę profili od nowa (po zmianach w panelu rodzica). Zwraca false, gdy aktywny profil zniknął albo urządzenie straciło dostęp. */
  refresh(): Promise<boolean>;
  syncState(): SyncState;
  syncDetail(): string;
  syncedAt(): string | null;
  syncNow(): Promise<SyncState>;
  /** Ranking graczy (patch-35): punkty rodzeństwa z serwera. Najpierw (na chwilę) dosyła zmiany tego dziecka, żeby jego wynik był świeży; w trybie lokalnym nie ma rankingu (`no-server`). */
  ranking(today: string): Promise<RankingResult>;
  /** Zdarzenia synchronizacji aktywnego profilu (także po zmianie profilu). Zwraca funkcję odpinającą. */
  onSync(fn: (e: AccountEvent) => void): () => void;
  /** Opis problemu z serwerem profili (np. brak migracji bazy), gdy aplikacja przeszła na tryb lokalny albo pracuje z pamięci; inaczej ''. */
  problem(): string;
}

export interface AccountsDeps {
  store: ProfileStore;
  /** Klucz urządzenia (tylko odczyt). */
  devices: { get(): unknown };
  client: ProfilesClient;
  /** Postęp w trybie lokalnym. */
  local: { load(): Progress; save(p: Progress): void };
  /** Wywoływane przy każdej zmianie wyglądu aktywnego profilu (motyw ma od razu się zastosować). */
  onLook?: (look: Look) => void;
  now?: () => Date;
  timers?: Timers;
  /** Jak długo czekamy na pierwsze pobranie postępu, zanim pokażemy ekran główny z tym, co jest. */
  firstSyncWaitMs?: number;
  /** Jak długo „Zmień profil” czeka na dosłanie zmian. */
  leaveWaitMs?: number;
  /** Jak długo ranking czeka na dosłanie punktów tego dziecka, zanim pobierze tabelę. */
  rankingSyncWaitMs?: number;
}

interface FamilySession {
  kind: 'family';
  profile: ProfileInfo;
  progress: Progress;
  sync: Sync;
  unsubscribe: () => void;
}
type Session = { kind: 'local'; progress: Progress } | FamilySession;

/** Komunikaty dla dziecka z „Mój wygląd” (zmiana imienia) i z „Kto się uczy?” — w obu językach. */
const MSG = {
  removed: { en: 'A parent removed this profile. Pick yours.', pl: 'Ten profil został usunięty przez rodzica. Wybierz swój.' },
  needLogin: { en: 'You can change the name after you pick your profile.', pl: 'Imię można zmienić po zalogowaniu profilu.' },
  badName: { en: 'Type a name or nickname (up to 20 characters).', pl: 'Wpisz imię albo pseudonim (do 20 znaków).' },
  taken: { en: 'This name is already used on this device. Choose another one.', pl: 'Takie imię już jest na tym urządzeniu. Wybierz inne.' },
  notSaved: { en: 'Could not save the name — check the internet and try again.', pl: 'Nie udało się zapisać imienia — sprawdź połączenie i spróbuj jeszcze raz.' },
} as const satisfies Record<string, Pair>;

const wait = <T>(p: Promise<T>, ms: number): Promise<T | undefined> =>
  new Promise((resolve) => {
    const t = setTimeout(() => resolve(undefined), ms);
    void p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      () => {
        clearTimeout(t);
        resolve(undefined);
      },
    );
  });

export function createAccounts(deps: AccountsDeps): Accounts {
  const { store, client } = deps;
  const firstSyncWaitMs = deps.firstSyncWaitMs ?? 6000;
  const leaveWaitMs = deps.leaveWaitMs ?? 2000;
  const rankingSyncWaitMs = deps.rankingSyncWaitMs ?? 2500;
  let session: Session | null = null;
  let known: ProfileInfo[] = [];
  let problemText = '';
  const listeners = new Set<(e: AccountEvent) => void>();

  const announceLook = (look: Look): void => deps.onLook?.(look);

  function endSession(): void {
    if (session?.kind === 'family') {
      session.sync.stop();
      session.unsubscribe();
    }
    session = null;
    announceLook(DEFAULT_LOOK);
  }

  /** Zapamiętuje listę profili (pamięć przeglądarki) i porządkuje lokalne skróty PIN-ów: profil, któremu rodzic zdjął PIN, nie powinien go już mieć w pamięci. */
  function learn(list: ProfileInfo[]): void {
    known = list;
    store.cacheProfiles(list);
    for (const p of list) if (!p.hasPin) store.setPinVerifier(p.id, null);
  }

  function enterLocal(): Entry {
    endSession();
    store.select(LOCAL_PROFILE);
    session = { kind: 'local', progress: deps.local.load() };
    announceLook(store.localLook());
    return { screen: 'home' };
  }

  async function open(profile: ProfileInfo): Promise<void> {
    endSession();
    store.select(profile.id);
    const hadCopy = store.hasProgress(profile.id);
    const progress = store.loadProgress(profile.id);
    const s = { kind: 'family', profile, progress } as FamilySession; // sync i unsubscribe uzupełniamy zaraz niżej (sync potrzebuje `s`)
    s.sync = createSync({
      profileId: profile.id,
      client,
      store,
      get: () => s.progress,
      replace: (p) => {
        s.progress = p;
      },
      ...(deps.now ? { now: deps.now } : {}),
      ...(deps.timers ? { timers: deps.timers } : {}),
    });
    s.unsubscribe = s.sync.subscribe((e) => {
      for (const fn of [...listeners]) fn({ ...e, profileId: profile.id });
    });
    session = s;
    announceLook(lookOf(profile));
    // Pierwsze wejście na tym urządzeniu (nowy tablet, wyczyszczona przeglądarka): chwilę czekamy na postęp z serwera, żeby dziecko nie zobaczyło zer.
    // Gdy kopia już jest, startujemy od razu, a serwer dosyła różnicę w tle.
    if (!hadCopy) await wait(s.sync.syncNow(), firstSyncWaitMs);
    else void s.sync.syncNow();
  }

  /** Start z pamięci przeglądarki, gdy serwer jest nieosiągalny (brak internetu albo błąd serwera). */
  async function fromCache(why: 'offline' | 'error'): Promise<Entry> {
    if (why === 'offline' && store.mode() === 'local') return enterLocal();
    const hasKey = deps.devices.get() !== null;
    const cached = store.cachedProfiles();
    if (!hasKey || cached.length === 0) return why === 'error' ? enterLocal() : { screen: 'offline' };
    known = cached;
    const chosen = cached.find((p) => p.id === store.selected());
    if (chosen) {
      await open(chosen);
      return { screen: 'home' };
    }
    endSession();
    return { screen: 'pick', profiles: cached, offline: true, canAdd: false };
  }

  const api: Accounts = {
    async start(lang = 'pl') {
      problemText = '';
      const r = await client.list();
      switch (r.kind) {
        case 'no-server':
          store.setMode('local');
          return enterLocal();
        case 'unpaired':
          store.setMode('family');
          endSession();
          store.select(null); // urządzenie wyłączone albo odłączone: sesja dziecka (i jego PIN) kończy się razem z nim
          return { screen: 'login', hadKey: deps.devices.get() !== null };
        case 'offline':
          return fromCache('offline');
        case 'error':
          problemText = r.detail;
          return fromCache('error');
        case 'ok': {
          store.setMode('family');
          learn(r.profiles);
          if (r.profiles.length === 0) {
            endSession();
            store.select(null);
            return { screen: 'create-first' };
          }
          // Profil z tej sesji (odświeżenie strony) wraca bez PIN-u; świeży start zawsze pokazuje wybór — także gdy profil jest tylko jeden.
          const selected = store.selected();
          const chosen = r.profiles.find((p) => p.id === selected);
          if (selected && !chosen) store.select(null); // wybrany profil został zarchiwizowany
          if (chosen) {
            // Serwer jest źródłem imienia i wyglądu (rodzic mógł je zmienić).
            await open(chosen);
            return { screen: 'home' };
          }
          endSession();
          const notice = selected ? MSG.removed[lang] : undefined;
          return { screen: 'pick', profiles: r.profiles, offline: false, canAdd: r.profiles.length < r.limit, ...(notice ? { notice } : {}) };
        }
      }
    },

    active: () => session !== null,
    mode: () => (session === null ? 'none' : session.kind),
    profile: () => (session?.kind === 'family' ? session.profile : null),
    profiles: () => known,
    look: () => (session?.kind === 'family' ? lookOf(session.profile) : session?.kind === 'local' ? store.localLook() : DEFAULT_LOOK),
    progress: () => session?.progress ?? emptyProgress(), // bez profilu (np. tuż po jego usunięciu) ekrany dostają pusty postęp zamiast wyjątku

    setProgress(p) {
      if (!session) return;
      session.progress = p;
      if (session.kind === 'local') {
        deps.local.save(p);
        return;
      }
      store.saveProgress(session.profile.id, p);
      session.sync.touch();
    },

    async select(id) {
      const profile = known.find((p) => p.id === id);
      if (!profile) return false;
      await open(profile);
      return true;
    },

    async adopt(profile) {
      learn([...known.filter((p) => p.id !== profile.id), profile]);
      await open(profile);
    },

    async verifyPin(id, pin) {
      const profile = known.find((p) => p.id === id);
      if (!profile?.hasPin) return { kind: 'ok' };
      if (!isPin(pin)) return { kind: 'bad', left: null };
      const r = await client.verifyPin(id, pin);
      if (r.kind === 'ok') {
        const digest = await localPinDigest(id, pin);
        if (digest) store.setPinVerifier(id, digest); // zapas na brak internetu
        return r;
      }
      if (r.kind !== 'offline' && r.kind !== 'error') return r;
      // Serwer niedostępny: porównujemy ze skrótem z ostatniego udanego wejścia. Bez niego wejście do profilu z PIN-em wymaga internetu.
      const saved = store.pinVerifier(id);
      if (saved === null) return r;
      return (await localPinDigest(id, pin)) === saved ? { kind: 'ok' } : { kind: 'bad', left: null };
    },

    route: () => store.session()?.route ?? HOME_ROUTE,
    // Przed ustaleniem profilu (start aplikacji) zapamiętany ekran z poprzedniego załadowania strony jeszcze czeka na odtworzenie — nie wolno go nadpisać.
    setRoute: (route) => {
      if (session !== null) store.setRoute(route);
    },
    welcomed: () => store.session()?.welcomed === true,
    markWelcomed: () => store.markWelcomed(),

    async leave() {
      if (session?.kind === 'family') await wait(session.sync.syncNow(), leaveWaitMs); // żeby ostatnia lekcja nie została tylko na tym tablecie
      endSession();
      store.select(null);
    },

    signOut() {
      endSession();
      store.select(null);
    },

    setLook(change) {
      if (!session) return;
      if (session.kind === 'local') {
        const look = lookOf({ ...store.localLook(), ...change });
        store.setLocalLook(look);
        announceLook(look);
        return;
      }
      const look = lookOf({ ...session.profile, ...change });
      const updated: ProfileInfo = { ...session.profile, ...look };
      session.profile = updated;
      learn(known.map((p) => (p.id === updated.id ? updated : p)));
      announceLook(look);
      // Na serwer idzie to, co faktycznie przyjęliśmy (niepoprawny awatar wrócił do domyślnego); nie udało się = serwer ma starszy wygląd, nic złego się nie dzieje.
      void client.setLook(updated.id, { ...(change.avatar !== undefined ? { avatar: look.avatar } : {}), ...(change.theme !== undefined ? { theme: look.theme } : {}) });
    },

    async rename(raw, lang = 'pl') {
      if (session?.kind !== 'family') return MSG.needLogin[lang];
      const name = cleanProfileName(raw);
      if (!name) return MSG.badName[lang];
      if (name === session.profile.name) return '';
      const id = session.profile.id;
      if (known.some((p) => p.id !== id && p.name.toLocaleLowerCase('pl') === name.toLocaleLowerCase('pl'))) return MSG.taken[lang];
      const saved = await client.setLook(id, { name });
      if (!saved) return MSG.notSaved[lang];
      if (session?.kind === 'family' && session.profile.id === id) session.profile = { ...session.profile, name: saved.name };
      learn(known.map((p) => (p.id === id ? { ...p, name: saved.name } : p)));
      return '';
    },

    async refresh() {
      const r = await client.list();
      if (r.kind === 'unpaired') {
        api.signOut();
        return false;
      }
      if (r.kind !== 'ok') return true; // brak odpowiedzi nie znaczy, że profil zniknął
      learn(r.profiles);
      if (session?.kind !== 'family') return true;
      const id = session.profile.id;
      const fresh = r.profiles.find((p) => p.id === id);
      if (!fresh) {
        endSession(); // wybór profilu zostaje w pamięci: przy starcie dziecko dostanie wyjaśnienie, czemu go nie ma
        return false;
      }
      session.profile = fresh;
      announceLook(lookOf(fresh));
      return true;
    },

    syncState: () => (session?.kind === 'family' ? session.sync.state() : 'idle'),
    syncDetail: () => (session?.kind === 'family' ? session.sync.detail() : ''),
    syncedAt: () => (session?.kind === 'family' ? session.sync.syncedAt() : null),
    syncNow: () => (session?.kind === 'family' ? session.sync.syncNow() : Promise.resolve('idle' as SyncState)),
    async ranking(today) {
      if (session?.kind !== 'family') return { kind: 'no-server' };
      await wait(session.sync.syncNow(), rankingSyncWaitMs);
      return client.ranking(today);
    },
    onSync(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    problem: () => problemText,
  };
  return api;
}
